import { useState, useCallback } from 'react'
import { useSearch } from 'features/search/context'

import { KojinResult } from 'interface/kojin'
import { useListAuthorLinks } from 'interface/author'

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  Tooltip,
  IconButton,
  DialogProps,
} from '@mui/material'

import {
  Fullscreen as FullScreenIcon,
  FullscreenExit as FullScreenExitIcon,
} from '@mui/icons-material'

import { CloseIcon } from 'shared/icons'
import { ErrorBlock } from 'components/controls/errorInfo'
import { Loading } from 'components/controls/loading'

import { AuthorGraph } from 'features/kojin/components/authorGraph'
import { KojinPreviewDialog } from '../preview'

type AuthorGraphDialogProps = {
  kojinList: KojinResult[] | null
  onClose: () => void
} & DialogProps
export const AuthorGraphDialog = ({
  kojinList,
  onClose,
  ...others
}: AuthorGraphDialogProps) => {
  const search = useSearch()

  const [fullScreen, setFullScreen] = useState<boolean>(false)
  const [selectedKojinCode, setSelectedKojinCode] = useState<string | null>(null)
  const [selectedAuthorId, setSelectedAuthorId] = useState<number | null>(null)

  const linkState = useListAuthorLinks(search?.searchId, { updateTime: search?.updateTime })

  const onSelectKojin = useCallback((kojinCode: string | null) => {
    setSelectedAuthorId(null)
    setSelectedKojinCode(kojinCode)
  }, [])

  const onSelectAuthor = useCallback((authorId: number | null) => {
    setSelectedKojinCode(null)
    setSelectedAuthorId(authorId)
  }, [])

  return (
    <Dialog
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      {...others}
    >
      <DialogTitle>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" color="text.primary" flexGrow={1}>
            {search?.title} 論文著者 相関図
          </Typography>
          {fullScreen ? (
            <Tooltip title="フルスクリーンをやめる">
              <IconButton onClick={() => setFullScreen(false)}>
                <FullScreenExitIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="フルスクリーン">
              <IconButton onClick={() => setFullScreen(true)}>
                <FullScreenIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="閉じる">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: 0,
          height: '80vh',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {linkState.loading && <Loading />}
        
        <ErrorBlock error={linkState.error} />

        <AuthorGraph
          kojinList={kojinList}
          linkList={linkState.data?.list || null}
          onSelectAuthor={onSelectAuthor}
          onSelectKojin={onSelectKojin}
        />
      </DialogContent>

      {(selectedKojinCode || selectedAuthorId) && (
        <KojinPreviewDialog
          kojinCode={selectedKojinCode ?? undefined}
          authorId={selectedAuthorId ?? undefined}
          onClose={() => onSelectKojin(null)}
        />
      )}
    </Dialog>
  )
}
